import { INVOICE_STATE_LABEL, PAYMENT_METHOD_LABEL, PAYMENT_MSG, type Payment } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { useLabels } from "../../labels.js";
import { md, yen } from "../../lib/num.js";
import { PaymentForm, invalidatePaymentViews } from "./PaymentForm.jsx";

/**
 * 入金の画面（/payments/:id）。上の欄と配分の表。「直す」（日・方法・メモ・配分）、「領収書をつくる」（1入金＝1領収書）、
 * 「入金削除」（領収書があれば 409 の文）。下に 登録した人・日時
 */
export function PaymentPage() {
  const { id = "" } = useParams();
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const q = useQuery({ queryKey: ["payment", id], queryFn: () => api<Payment>("GET", `/payments/${id}`), retry: false });
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 入金の一覧
  const back = useBackNav({ to: "/payments", label: "入金" });
  usePageName(q.data ? `入金 ${yen(q.data.amount)} 円` : null);
  const [edit, setEdit] = useState(false);
  const [askVoid, setAskVoid] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p><Link className="btn text" to="/payments">入金一覧へ</Link></p>
      </div>
    );
  }
  const p = q.data;
  const voided = !!p.voidedAt;

  const makeReceipt = async () => {
    setErr(undefined);
    try {
      const r = await api<{ id: string }>("POST", "/receipts", { fromPaymentId: p.id });
      invalidatePaymentViews(qc);
      nav(`/receipts/${r.id}`, { state: { fresh: true, notice: "入金から領収書を作成しました。" } });
    } catch (e) {
      if (e instanceof ApiError && typeof e.problem.receiptId === "string") {
        nav(`/receipts/${e.problem.receiptId}`);
        return;
      }
      setErr(messageOf(e));
    }
  };
  const doVoid = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const r = await api<Payment>("POST", `/payments/${p.id}/void`);
      qc.setQueryData(["payment", p.id], r);
      invalidatePaymentViews(qc);
      setAskVoid(false);
      toast(PAYMENT_MSG.voidedDone);
    } catch (e) {
      setErr(messageOf(e));
      setAskVoid(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>
          入金 <span className="num">{yen(p.amount)}</span> 円 {voided ? <span className="chip" title={`${md(p.voidedAt!.slice(0, 10))} に削除。請求書の残額には数えていません`} data-testid="payment-voided">削除 {md(p.voidedAt!.slice(0, 10))}</span> : null}
          {/* 当てていない額（#168：帯 → 見出しの右のチップ） */}
          {!voided && p.unallocated > 0 ? (
            <>
              <span className="chip ai" data-testid="payment-unallocated-band">未消込 <span className="num">{yen(p.unallocated)}</span> 円</span>
              <Help testId="help-unallocated" text="前受金・過入金です。請求書を発行したら「修正」で消込できます。" />
            </>
          ) : null}
        </h1>
        <span className="sp" />
        {!voided && !edit ? (
          <>
            <button type="button" className="small" onClick={() => setEdit(true)} data-testid="payment-edit">修正</button>
            {p.receipt ? (
              <Link className="btn" to={`/receipts/${p.receipt.id}`} data-testid="payment-receipt-link">
                {p.receipt.status === "draft" ? "領収書下書き表示" : `領収書 ${p.receipt.documentNo ?? ""} 表示`}
              </Link>
            ) : (
              <button type="button" className="btn primary" disabled={p.allocated === 0} onClick={() => void makeReceipt()} data-testid="payment-make-receipt">領収書作成</button>
            )}
          </>
        ) : null}
      </div>
      <ErrorText>{err}</ErrorText>
      {edit ? (
        <PaymentForm
          mode="edit"
          initialPartner={p.partner}
          initialAmount={p.amount}
          payment={p}
          onDone={(np) => {
            setEdit(false);
            if (np !== p) toast(PAYMENT_MSG.updated);
          }}
        />
      ) : (
        <>
          <section className="paper" data-testid="payment-head">
            <dl className="kv">
              <dt>{L.customer}</dt>
              <dd><Link to={`/partners/${p.partner.id}`}>{p.partner.name}</Link> <span className="muted">{p.partner.honorific}</span></dd>
              <dt>入金日</dt>
              <dd className="num">{md(p.receivedOn)}</dd>
              <dt>方法</dt>
              <dd>{PAYMENT_METHOD_LABEL[p.method]}</dd>
              <dt>入金額</dt>
              <dd className="num">{yen(p.amount)} 円</dd>
              {p.memo ? (
                <>
                  <dt>メモ</dt>
                  <dd style={{ whiteSpace: "pre-wrap" }}>{p.memo}</dd>
                </>
              ) : null}
            </dl>
          </section>
          <h2 style={{ fontSize: "var(--fs-19)", margin: "20px 0 8px" }}>消込した請求書</h2>
          {p.allocations.length === 0 ? (
            <p className="hint">消込した請求書はありません。</p>
          ) : (
            <div className="tbl-wrap">
              <table className="list" data-testid="payment-allocations">
                <thead>
                  <tr>
                    <th>番号</th>
                    <th>件名</th>
                    <th>お支払期限</th>
                    <th className="amt">消込額</th>
                    <th className="amt">手数料・値引き</th>
                    <th>請求書の現在の状態</th>
                  </tr>
                </thead>
                <tbody>
                  {p.allocations.map((a) => (
                    <tr key={a.documentId} className="r" tabIndex={0} onClick={() => nav(`/invoices/${a.documentId}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/invoices/${a.documentId}`)}>
                      <td className="num">{a.documentNo}</td>
                      <td>{a.subject}</td>
                      <td className="num">{a.dueDate ? md(a.dueDate) : "—"}</td>
                      <td className="amt num">{yen(a.amount)} 円</td>
                      <td className="amt num">
                        {a.fee ? <div>手数料 {yen(a.fee)} 円</div> : null}
                        {a.discount ? <div>値引き {yen(a.discount)} 円</div> : null}
                        {!a.fee && !a.discount ? <span className="muted">—</span> : null}
                      </td>
                      <td data-testid="alloc-state">
                        {INVOICE_STATE_LABEL[a.state]}
                        {a.balance > 0 ? <span className="muted">　残 <span className="num">{yen(a.balance)}</span> 円</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="hint" style={{ marginTop: 12 }}>
            登録：{p.createdByName || "—"}（{md(p.createdAt.slice(0, 10))}）
          </p>
          {!voided ? (
            <div style={{ marginTop: 16 }}>
              {askVoid ? (
                <div className="next" role="alertdialog" aria-label="入金削除" data-testid="void-confirm">
                  <span>この入金を削除しますか。消込していた請求書は残額に戻ります（記録は残ります）。</span>
                  <button type="button" className="btn" disabled={busy} onClick={() => void doVoid()} data-testid="void-yes">削除</button>
                  <button type="button" className="small" onClick={() => setAskVoid(false)}>取消</button>
                </div>
              ) : (
                <button type="button" className="small" onClick={() => setAskVoid(true)} data-testid="payment-void">入金削除</button>
              )}
            </div>
          ) : null}
        </>
      )}
      {toastNode}
    </>
  );
}
